import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Receipt, RefreshCw, Euro, CalendarRange,
  ChevronLeft, ChevronRight, Loader2,
  FileText, Shield, CheckCircle2, Trash2, AlertTriangle, ChevronDown,
  Search, ArrowRight, CreditCard, X
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';
import ReglementPanel from '../components/ReglementPanel';

const MOIS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const STATUT_STYLES = {
  confirme: { bg: '#FEFCE8', color: '#A16207', label: 'Confirmé' },
  en_cours: { bg: '#FFF7ED', color: '#C2410C', label: 'Enlevé' },
  retour:   { bg: '#F0FDF4', color: '#15803D', label: 'Retourné' },
};

function fmtEur(v) {
  return (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function fmtDate(s) {
  if (!s) return '';
  return s.split('-').reverse().join('/');
}

// ── Panneau détail ────────────────────────────────────────────────────────────
function DetailPanel({ contrat, facturant, onFacturer, factureCree, onOpenPlPanel }) {
  if (!contrat) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 py-24">
        <FileText size={36} style={{ color: 'var(--text-disabled)' }} />
        <p className="text-sm font-medium" style={{ color: 'var(--text-placeholder)' }}>
          Sélectionnez un contrat pour voir le détail
        </p>
      </div>
    );
  }

  const st = STATUT_STYLES[contrat.statut] || { bg: '#F3F4F6', color: '#6B7280', label: contrat.statut };
  const lignesMat  = (contrat.lignes || []).filter(l => !l.is_assurance && !l.is_prestation);
  const lignesAss  = (contrat.lignes || []).filter(l => l.is_assurance);
  const lignesPrest = (contrat.lignes || []).filter(l => l.is_prestation);
  const totalHT    = contrat.total_a_facturer || 0;
  const totalTVA   = (contrat.lignes || []).reduce((s, l) => s + (l.montant_ht * (l.taux_tva / 100) || 0), 0);
  const totalTTC   = totalHT + totalTVA;

  return (
    <div className="flex flex-col h-full">
      {/* En-tête contrat */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.open(`/contrats?contrat_id=${contrat.contrat_id}`, '_blank')}
                title="Ouvrir le contrat dans un nouvel onglet"
                className="text-base font-bold hover:underline transition-colors"
                style={{ color: 'var(--primary-solid)' }}>
                {contrat.numero}
              </button>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: st.bg, color: st.color }}>
                {st.label}
              </span>
            </div>
            <p className="text-sm mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>
              {contrat.client_nom}
            </p>
            <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: 'var(--text-disabled)' }}>
              <CalendarRange size={11} />
              Période facturée : {fmtDate(contrat.periode_debut)} → {fmtDate(contrat.periode_fin)}
            </p>
          </div>
          {factureCree ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold"
                style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
                <CheckCircle2 size={14} />
                {factureCree.ext_invoice_number
                  ? `Brouillon ${factureCree.ext_invoice_number}`
                  : factureCree.odoo_invoice_name
                    ? `Brouillon ${factureCree.odoo_invoice_name}`
                    : 'Facture créée'}
              </div>
              {factureCree.facture_id && factureCree.ext_invoice_number && (
                <button
                  onClick={() => onOpenPlPanel?.(factureCree.facture_id)}
                  data-testid="btn-voir-brouillon-pdf"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white flex-shrink-0"
                  style={{ background: 'var(--primary-gradient)' }}>
                  <FileText size={14} />
                  Brouillon PDF
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => onFacturer(contrat.contrat_id, contrat.periode_debut, contrat.periode_fin)}
              disabled={facturant === contrat.contrat_id}
              data-testid={`btn-facturer-${contrat.contrat_id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white flex-shrink-0 transition-opacity"
              style={{ background: 'var(--primary-gradient)', opacity: facturant === contrat.contrat_id ? 0.7 : 1 }}>
              {facturant === contrat.contrat_id
                ? <Loader2 size={14} className="animate-spin" />
                : <Receipt size={14} />}
              {facturant === contrat.contrat_id ? 'Création...' : 'Créer la facture'}
            </button>
          )}
        </div>
      </div>

      {/* Tableau unifié — alignement parfait des colonnes */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {(lignesMat.length > 0 || lignesAss.length > 0 || lignesPrest.length > 0) && (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col />                           {/* Désignation — flexible */}
                <col style={{ width: '136px' }} /> {/* Période */}
                <col style={{ width: '68px' }} />  {/* Qté */}
                <col style={{ width: '108px' }} /> {/* PU */}
                <col style={{ width: '112px' }} /> {/* Total HT */}
                <col style={{ width: '76px' }} />  {/* TVA % */}
                <col style={{ width: '104px' }} /> {/* Mnt TVA */}
              </colgroup>
              <thead>
                <tr style={{ background: 'var(--bg-subtle, #F9FAFB)', borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Désignation</th>
                  <th className="text-center px-2 py-2.5 text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Période</th>
                  <th className="text-center px-2 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Qté</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>PU</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Total HT</th>
                  <th className="text-right px-2 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>TVA %</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Mnt TVA</th>
                </tr>
              </thead>
              <tbody>

                {/* ── Section Location ── */}
                {lignesMat.length > 0 && (
                  <>
                    <tr style={{ background: '#F9FAFB', borderTop: '1px solid var(--border)' }}>
                      <td colSpan={7} className="px-4 py-1.5">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                          Location
                        </span>
                      </td>
                    </tr>
                    {lignesMat.map((l, i) => (
                      <React.Fragment key={`loc-${i}`}>
                      <tr style={{ borderTop: '1px solid #F2F2EE' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-sm truncate" style={{ color: 'var(--text)' }}>{l.gamme_nom}</p>
                          {l.categorie_nom && (
                            <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{l.categorie_nom}</p>
                          )}
                          {l.compte_produit && (
                            <p className="text-xs mt-0.5 font-mono truncate" style={{ color: 'var(--text-disabled)' }}>Cpte {l.compte_produit}</p>
                          )}
                        </td>
                        <td className="px-2 py-3 text-center text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                          {fmtDate(l.date_debut)}<br />{fmtDate(l.date_fin)}
                        </td>
                        <td className="px-2 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold"
                            style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                            {l.nb_jours}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmtEur(l.prix_jour)}</td>
                        <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums" style={{ color: 'var(--text)' }}>{fmtEur(l.montant_ht)}</td>
                        <td className="px-2 py-3 text-right text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          {l.taux_tva > 0 ? `${l.taux_tva}%` : '—'}
                        </td>
                        <td className="px-3 py-3 text-right text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          {l.taux_tva > 0 ? fmtEur((l.montant_ht || 0) * l.taux_tva / 100) : '—'}
                        </td>
                      </tr>
                      {/* ── Détail suspensions ── */}
                      {Array.isArray(l.suspensions_detail) && l.suspensions_detail.map((s, si) => (
                        <tr key={`sus-${i}-${si}`} style={{ background: '#FFF7ED', borderTop: '1px solid #FED7AA' }}>
                          <td colSpan={3} className="px-4 py-1.5">
                            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#C2410C' }}>
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em' }}>↳ SUSPENSION</span>
                              <span className="font-medium">{s.motif}</span>
                              <span style={{ color: '#9A3412' }}>—</span>
                              <span>{fmtDate(s.date_debut)}{s.date_debut !== s.date_fin ? ` → ${fmtDate(s.date_fin)}` : ''}</span>
                            </div>
                          </td>
                          <td colSpan={4} className="px-3 py-1.5 text-right">
                            <span className="text-xs font-semibold tabular-nums" style={{ color: '#C2410C' }}>
                              −{s.nb_jours} j. déduit{s.nb_jours > 1 ? 's' : ''}
                            </span>
                          </td>
                        </tr>
                      ))}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {/* ── Section Assurance ── */}
                {lignesAss.length > 0 && (
                  <>
                    <tr style={{ background: '#EFF6FF', borderTop: '1px solid #BFDBFE' }}>
                      <td colSpan={7} className="px-4 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <Shield size={11} style={{ color: '#1D4ED8' }} />
                          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#1D4ED8' }}>Assurance</span>
                        </div>
                      </td>
                    </tr>
                    {lignesAss.map((l, i) => {
                      const mntTva = (l.montant_ht || 0) * ((l.taux_tva || 0) / 100);
                      return (
                        <tr key={`ass-${i}`} style={{ borderTop: '1px solid #DBEAFE', background: '#F0F9FF' }}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm truncate" style={{ color: '#0369A1' }}>{l.gamme_nom}</p>
                            {l.compte_produit && (
                              <p className="text-xs mt-0.5 font-mono truncate" style={{ color: '#7CB9E8' }}>Cpte {l.compte_produit}</p>
                            )}
                          </td>
                          <td className="px-2 py-3 text-center text-xs" style={{ color: '#93C5FD' }}>—</td>
                          <td className="px-2 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold"
                              style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                              {l.nb_jours}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right text-sm tabular-nums" style={{ color: '#0369A1' }}>{fmtEur(l.prix_jour)}</td>
                          <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums" style={{ color: '#0369A1' }}>{fmtEur(l.montant_ht)}</td>
                          <td className="px-2 py-3 text-right text-xs tabular-nums" style={{ color: '#0369A1' }}>
                            {(l.taux_tva || 0) > 0 ? `${l.taux_tva}%` : '—'}
                          </td>
                          <td className="px-3 py-3 text-right text-xs tabular-nums" style={{ color: '#0369A1' }}>
                            {mntTva > 0 ? fmtEur(mntTva) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}

                {/* ── Section Prestations ── */}
                {lignesPrest.length > 0 && (
                  <>
                    <tr style={{ background: '#F9FAFB', borderTop: '1px solid var(--border)' }}>
                      <td colSpan={7} className="px-4 py-1.5">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                          Prestations
                        </span>
                      </td>
                    </tr>
                    {lignesPrest.map((l, i) => {
                      const mntTva = (l.montant_ht || 0) * ((l.taux_tva || 0) / 100);
                      return (
                        <tr key={`prest-${i}`} style={{ borderTop: '1px solid #F2F2EE' }}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm truncate" style={{ color: 'var(--text)' }}>{l.gamme_nom}</p>
                            {l.compte_produit && (
                              <p className="text-xs mt-0.5 font-mono truncate" style={{ color: 'var(--text-disabled)' }}>Cpte {l.compte_produit}</p>
                            )}
                          </td>
                          <td className="px-2 py-3 text-center text-xs" style={{ color: 'var(--text-disabled)' }}>—</td>
                          <td className="px-2 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold"
                              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                              {l.nb_jours}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmtEur(l.prix_jour)}</td>
                          <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums" style={{ color: 'var(--text)' }}>{fmtEur(l.montant_ht)}</td>
                          <td className="px-2 py-3 text-right text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                            {(l.taux_tva || 0) > 0 ? `${l.taux_tva}%` : '—'}
                          </td>
                          <td className="px-3 py-3 text-right text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                            {mntTva > 0 ? fmtEur(mntTva) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}

              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pied de page totaux */}
      <div className="px-6 py-4 border-t space-y-1.5" style={{ borderColor: 'var(--border)', background: '#FAFAFA' }}>
        <div className="flex justify-between text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Total HT</span>
          <span className="font-semibold tabular-nums" style={{ color: 'var(--text)' }}>{fmtEur(totalHT)}</span>
        </div>
        {totalTVA > 0 && (
          <div className="flex justify-between text-sm">
            <span style={{ color: 'var(--text-muted)' }}>TVA</span>
            <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmtEur(totalTVA)}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
          <span style={{ color: 'var(--text)' }}>Total TTC</span>
          <span className="tabular-nums" style={{ color: 'var(--primary-solid)' }}>{fmtEur(totalTTC)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────
export default function AideFacturationLocation({ embedded = false, initialContratId, onFactureCreee }) {
  const today = new Date();
  const { activeAgences } = useSociete();
  const [mois, setMois]         = useState(today.getMonth() + 1);
  const [annee, setAnnee]       = useState(today.getFullYear());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);   // contrat sélectionné
  const [facturant, setFacturant] = useState(null);
  const [purgingId, setPurgingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [showBloquees, setShowBloquees] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null); // null | 'retourne' | 'comptant' | 'fin_mois'
  const [search, setSearch] = useState('');
  // Ref anti-race-condition : seul le dernier appel load() met à jour l'état
  const loadCounterRef = useRef(0);
  // Anti-fermeture panneau après facturation : ref (pour closures load) + state (pour re-renders)
  const factureCreeContratIdRef = useRef(null);
  const [factureCreeData, setFactureCreeData] = useState(null);
  // Panel brouillon Pennylane (identique à Ventes.js)
  const [plDraftPanel, setPlDraftPanel]           = useState(null); // null | { loading, pdf_blob_url }
  const [validantPL, setValidantPL]               = useState(false);
  const [supprimantDraftPL, setSupprimantDraftPL] = useState(false);
  const [showReglementPanel, setShowReglementPanel] = useState(false);
  const [reglementSaisi, setReglementSaisi]       = useState(null);

  // Liste brute depuis l'API
  const allContrats = useMemo(() => data?.contrats || [], [data]);

  // Compteurs pour les cards
  const counts = useMemo(() => ({
    retourne: allContrats.filter(c => c.statut === 'retour' && c.mode_reglement !== 'comptant').length,
    comptant: allContrats.filter(c => c.mode_reglement === 'comptant' && c.statut !== 'retour').length,
    fin_mois: allContrats.filter(c => c.depasse_periode && c.statut !== 'retour' && c.mode_reglement !== 'comptant').length,
  }), [allContrats]);

  // Liste filtrée (card + recherche)
  const filteredContrats = useMemo(() => {
    let list = allContrats;
    if (activeFilter === 'retourne')  list = list.filter(c => c.statut === 'retour' && c.mode_reglement !== 'comptant');
    else if (activeFilter === 'comptant') list = list.filter(c => c.mode_reglement === 'comptant' && c.statut !== 'retour');
    else if (activeFilter === 'fin_mois') list = list.filter(c => c.depasse_periode && c.statut !== 'retour' && c.mode_reglement !== 'comptant');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.numero?.toLowerCase().includes(q) ||
        c.client_nom?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allContrats, activeFilter, search]);

  // Factures bloquantes filtrées par recherche
  const filteredFactures = useMemo(() => {
    const all = data?.factures_en_attente || [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(f =>
      f.contrat_numero?.toLowerCase().includes(q) ||
      f.client_nom?.toLowerCase().includes(q) ||
      f.odoo_invoice_name?.toLowerCase().includes(q)
    );
  }, [data?.factures_en_attente, search]);

  // Désélectionner si le contrat actif n'est plus dans la liste filtrée
  // Exception : si une facture vient d'être créée pour ce contrat, garder le panneau ouvert
  useEffect(() => {
    if (selected && !filteredContrats.find(c => c.contrat_id === selected.contrat_id)) {
      if (factureCreeData?.contrat_id === selected.contrat_id) return;
      setSelected(null);
    }
  }, [filteredContrats, selected, factureCreeData]);

  const load = useCallback(async () => {
    const callId = ++loadCounterRef.current;
    setLoading(true);
    try {
      const r = await api.getAideFacturation({ mois, annee });
      // Ignorer les réponses d'appels obsolètes (race condition)
      if (callId !== loadCounterRef.current) return;
      setData(r.data);
      // Si le contrat sélectionné est toujours dans la liste, on le rafraîchit.
      // Si le contrat vient d'être facturé (plus dans la liste), on garde l'état pour affichage.
      setSelected(prev => {
        if (!prev) return null;
        const found = r.data?.contrats?.find(c => c.contrat_id === prev.contrat_id);
        if (found) return found;
        if (factureCreeContratIdRef.current === prev.contrat_id) return prev;
        return null;
      });
    } catch {
      if (callId === loadCounterRef.current) toast.error('Erreur de chargement');
    } finally {
      if (callId === loadCounterRef.current) setLoading(false);
    }
  }, [mois, annee, activeAgences]);

  useEffect(() => { load(); }, [load]);

  // Auto-sélection du contrat cible (depuis le bouton "Créer/voir facture" dans Locations)
  // + pré-filtre la liste de gauche sur ce contrat (search pré-rempli avec le numéro)
  useEffect(() => {
    if (!initialContratId || !data?.contrats) return;
    const c = data.contrats.find(c => c.contrat_id === initialContratId);
    if (c) {
      setSelected(c);
      setSearch(c.numero); // filtre la liste de gauche sur ce contrat uniquement
    } else {
      // Contrat non trouvé dans la période courante → afficher un toast informatif
      toast.warning(`Le contrat n'est pas visible dans la période ${mois}/${annee}. Vérifiez le mois ou les filtres.`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const naviguer = (delta) => {
    let m = mois + delta;
    let a = annee;
    if (m > 12) { m = 1; a++; }
    if (m < 1)  { m = 12; a--; }
    factureCreeContratIdRef.current = null;
    setFactureCreeData(null);
    setSelected(null);
    setSearch('');
    setData(null);  // Effacer immédiatement les données périmées → total à 0 pendant le chargement
    setMois(m); setAnnee(a);
  };

  const facturer = async (contrat_id, periode_debut, periode_fin) => {
    setFacturant(contrat_id);
    try {
      const r = await api.creerFactureLocation(contrat_id, { periode_debut, periode_fin });
      // Marquer AVANT load() pour que le panneau reste ouvert même si le contrat disparaît de la liste
      factureCreeContratIdRef.current = contrat_id;
      setFactureCreeData({ ...r.data, contrat_id });
      const num = r.data?.ext_invoice_number || r.data?.odoo_invoice_name || '';
      toast.success(num ? `Brouillon créé : ${num}` : 'Facture créée (brouillon)');
      await load();
      // NE PAS appeler onFactureCreee() ici : ça changerait d'onglet → démontage → panel impossible
      // Ouvrir le panel quoi qu'il arrive dès qu'on a un facture_id (même si PL a échoué)
      if (r.data?.facture_id) {
        await openPlDraftPanel(r.data.facture_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setFacturant(null);
    }
  };

  const purgerFacture = async (facture_id, contrat_numero) => {
    if (!window.confirm(
      `Supprimer la facture locale du contrat ${contrat_numero} ?\n\n` +
      `Cela débloquera le contrat pour une nouvelle facturation.\n` +
      `Si la facture existe encore dans Odoo en brouillon, elle sera annulée automatiquement.`
    )) return;
    setPurgingId(facture_id);
    try {
      await api.supprimerFactureLocation(facture_id);
      toast.success(`Facture purgée — contrat ${contrat_numero} débloqué`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setPurgingId(null);
    }
  };

  const syncerFacture = async (facture_id) => {
    setSyncingId(facture_id);
    try {
      const res = await api.syncFactureOdoo(facture_id);
      const statut = res.data?.statut || res.data?.odoo_statut || '?';
      toast.success(`Statut mis à jour : ${statut}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de synchroniser depuis Odoo');
    } finally {
      setSyncingId(null);
    }
  };

  // ── Panel brouillon Pennylane ────────────────────────────────────────────────

  const openPlDraftPanel = async (facture_id) => {
    setPlDraftPanel({ loading: true, pdf_blob_url: null });
    const reglementPromise = api.getReglementsFactureLoc(facture_id).then(r => r.data).catch(() => null);
    try {
      const [resp, reglData] = await Promise.all([
        api.getPlFactureLocPdfBlob(facture_id),
        reglementPromise,
      ]);
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      setPlDraftPanel({ loading: false, pdf_blob_url: blobUrl });
      if (reglData?.reglements?.length > 0) setReglementSaisi(reglData);
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? null : (e.response?.data?.detail || null);
      if (detail) toast.error(detail);
      setPlDraftPanel({ loading: false, pdf_blob_url: null });
    }
  };

  const closePlDraftPanel = () => {
    if (plDraftPanel?.pdf_blob_url) URL.revokeObjectURL(plDraftPanel.pdf_blob_url);
    setPlDraftPanel(null);
    setShowReglementPanel(false);
    setReglementSaisi(null);
  };

  const handleValiderPennylane = async () => {
    if (!factureCreeData?.facture_id) return;
    setValidantPL(true);
    try {
      await api.validerPlFactureLoc(factureCreeData.facture_id);
      toast.success('Facture validée dans Pennylane');
      setFactureCreeData(prev => prev ? { ...prev, ext_status: 'posted' } : prev);
      closePlDraftPanel();
      await load();
      onFactureCreee?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation Pennylane');
    } finally {
      setValidantPL(false);
    }
  };

  const handleSupprimerBrouillonPennylane = async () => {
    if (!factureCreeData?.facture_id) return;
    if (!window.confirm('Supprimer ce brouillon dans Pennylane et remettre le contrat à zéro ?')) return;
    setSupprimantDraftPL(true);
    try {
      await api.supprimerPlDraftLoc(factureCreeData.facture_id);
      toast.success('Brouillon supprimé — vous pouvez refacturer');
      factureCreeContratIdRef.current = null;
      setFactureCreeData(null);
      closePlDraftPanel();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression brouillon');
    } finally {
      setSupprimantDraftPL(false);
    }
  };

  const totalMois = filteredContrats.reduce((s, c) => s + (c.total_a_facturer || 0), 0);

  return (
    <div className="flex flex-col" style={embedded ? { height: '100%', minHeight: 0 } : { height: 'calc(100vh - 48px)', minHeight: 0, margin: '-24px' }}>
      {/* ── Barre supérieure ── */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>
              Aide à la facturation
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Contrats avec des lignes non facturées sur la période sélectionnée
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Navigateur mois */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white"
              style={{ borderColor: 'var(--border)' }}>
              <button onClick={() => naviguer(-1)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <ChevronLeft size={15} />
              </button>
              <span className="text-sm font-bold min-w-36 text-center" style={{ color: 'var(--text)' }}>
                {MOIS_FR[mois - 1]} {annee}
              </span>
              <button onClick={() => naviguer(+1)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <ChevronRight size={15} />
              </button>
            </div>

            <button onClick={load} disabled={loading}
              className="p-2 rounded-lg border hover:bg-gray-50 transition-colors bg-white"
              style={{ borderColor: 'var(--border)' }}>
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>

            {/* Total */}
            {data && !loading && totalMois > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                <Euro size={15} />
                <span className="text-sm font-bold tabular-nums">
                  {fmtEur(totalMois)} à facturer
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Cards filtrantes + Barre de recherche ── */}
      {!loading && allContrats.length > 0 && (
        <div className="flex-shrink-0 px-6 py-3 border-b flex items-center gap-2 flex-wrap"
          style={{ borderColor: 'var(--border)', background: '#FAFAFA' }}>

          {/* Card Tous */}
          <button
            data-testid="filter-card-tous"
            onClick={() => setActiveFilter(null)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all"
            style={{
              background: activeFilter === null ? 'var(--primary-solid)' : 'white',
              borderColor: activeFilter === null ? 'var(--primary-solid)' : 'var(--border)',
              color: activeFilter === null ? 'white' : 'var(--text-muted)',
            }}>
            <Receipt size={13} />
            Tous
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{
                background: activeFilter === null ? '#ffffff30' : 'var(--primary-50)',
                color: activeFilter === null ? 'white' : 'var(--primary-solid)',
              }}>
              {allContrats.length}
            </span>
          </button>

          {/* Card Retourné */}
          <button
            data-testid="filter-card-retourne"
            onClick={() => setActiveFilter(v => v === 'retourne' ? null : 'retourne')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all"
            style={{
              background: activeFilter === 'retourne' ? '#C2410C' : 'white',
              borderColor: activeFilter === 'retourne' ? '#C2410C' : 'var(--border)',
              color: activeFilter === 'retourne' ? 'white' : '#C2410C',
            }}>
            <CheckCircle2 size={13} />
            Retourné
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{
                background: activeFilter === 'retourne' ? '#ffffff30' : '#FFF7ED',
                color: activeFilter === 'retourne' ? 'white' : '#C2410C',
              }}>
              {counts.retourne}
            </span>
          </button>

          {/* Card Comptant */}
          <button
            data-testid="filter-card-comptant"
            onClick={() => setActiveFilter(v => v === 'comptant' ? null : 'comptant')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all"
            style={{
              background: activeFilter === 'comptant' ? '#15803D' : 'white',
              borderColor: activeFilter === 'comptant' ? '#15803D' : 'var(--border)',
              color: activeFilter === 'comptant' ? 'white' : '#15803D',
            }}>
            <CreditCard size={13} />
            Comptant
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{
                background: activeFilter === 'comptant' ? '#ffffff30' : '#F0FDF4',
                color: activeFilter === 'comptant' ? 'white' : '#15803D',
              }}>
              {counts.comptant}
            </span>
          </button>

          {/* Card Fin de mois */}
          <button
            data-testid="filter-card-fin-mois"
            onClick={() => setActiveFilter(v => v === 'fin_mois' ? null : 'fin_mois')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all"
            style={{
              background: activeFilter === 'fin_mois' ? '#4F46E5' : 'white',
              borderColor: activeFilter === 'fin_mois' ? '#4F46E5' : 'var(--border)',
              color: activeFilter === 'fin_mois' ? 'white' : '#4F46E5',
            }}>
            <ArrowRight size={13} />
            Fin de mois
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{
                background: activeFilter === 'fin_mois' ? '#ffffff30' : '#EEF2FF',
                color: activeFilter === 'fin_mois' ? 'white' : '#4F46E5',
              }}>
              {counts.fin_mois}
            </span>
          </button>

          {/* Barre de recherche */}
          <div className="ml-auto flex items-center gap-2 px-3 py-2 rounded-xl border bg-white"
            style={{ borderColor: 'var(--border)', minWidth: 200 }}>
            <Search size={13} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} />
            <input
              data-testid="search-aide-facturation"
              type="text"
              placeholder="N° contrat, facture, client..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-xs bg-transparent outline-none min-w-0"
              style={{ color: 'var(--text)' }}
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="text-xs px-1 rounded"
                style={{ color: 'var(--text-disabled)' }}>
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Corps : 2 colonnes ── */}
      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : allContrats.length === 0 ? (
        <div className="flex flex-col items-center gap-3 flex-1 justify-center">
          <CheckCircle2 size={36} style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            Aucun contrat à facturer pour {MOIS_FR[mois - 1]} {annee}
          </p>
          {data?.factures_en_attente?.length > 0 && (
            <div className="mt-4 w-full max-w-md px-4">
              <div className="flex items-center gap-2 px-3 py-2 rounded-t-xl border-b"
                style={{ background: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}>
                <AlertTriangle size={13} />
                <span className="text-xs font-bold">
                  {data.factures_en_attente.length} facture{data.factures_en_attente.length > 1 ? 's' : ''} bloquante{data.factures_en_attente.length > 1 ? 's' : ''} détectée{data.factures_en_attente.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="border border-t-0 rounded-b-xl overflow-hidden divide-y" style={{ borderColor: '#FECACA', divideColor: '#FDE68A' }}>
                {data.factures_en_attente.map(f => (
                  <div key={f.facture_id} data-testid={`facture-bloquante-${f.facture_id}`}
                    className="flex items-center gap-3 px-3 py-2.5"
                    style={{ background: '#FFFBEB' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold" style={{ color: '#92400E' }}>
                        {f.contrat_numero || f.contrat_id} — {f.client_nom}
                      </p>
                      <p className="text-xs" style={{ color: '#B45309' }}>
                        {fmtDate(f.periode_debut)} → {fmtDate(f.periode_fin)} · {fmtEur(f.total_ht)} HT
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => syncerFacture(f.facture_id)} disabled={syncingId === f.facture_id}
                        data-testid={`btn-sync-${f.facture_id}`}
                        title="Synchroniser depuis Odoo"
                        className="flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium"
                        style={{ borderColor: '#BAE6FD', background: '#F0F9FF', color: '#0369A1' }}>
                        {syncingId === f.facture_id
                          ? <Loader2 size={11} className="animate-spin" />
                          : <RefreshCw size={11} />}
                        <span>Sync</span>
                      </button>
                      <button onClick={() => purgerFacture(f.facture_id, f.contrat_numero || f.contrat_id)}
                        disabled={purgingId === f.facture_id}
                        data-testid={`btn-purger-${f.facture_id}`}
                        title="Purger cette facture locale"
                        className="p-1.5 rounded-lg border" style={{ borderColor: '#FECACA', background: '#FEF2F2' }}>
                        {purgingId === f.facture_id
                          ? <Loader2 size={12} className="animate-spin" style={{ color: '#B91C1C' }} />
                          : <Trash2 size={12} style={{ color: '#B91C1C' }} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* ── Colonne gauche : liste ── */}
          <div className="w-80 flex-shrink-0 border-r overflow-y-auto" style={{ borderColor: 'var(--border)' }}>
            {/* ── Section factures bloquantes (priorité haute) ── */}
            {filteredFactures.length > 0 && (
              <div className="mx-3 mt-3 mb-2">
                <button
                  data-testid="toggle-factures-bloquantes"
                  onClick={() => setShowBloquees(v => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border"
                  style={{ background: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' }}>
                  <AlertTriangle size={12} />
                  <span className="flex-1 text-left">
                    {filteredFactures.length} facture{filteredFactures.length > 1 ? 's' : ''} bloquante{filteredFactures.length > 1 ? 's' : ''}
                  </span>
                  <ChevronDown size={12} style={{ transform: showBloquees ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </button>
                {showBloquees && (
                  <div className="mt-1.5 space-y-1.5">
                    {filteredFactures.map(f => (
                      <div key={f.facture_id} data-testid={`facture-bloquante-${f.facture_id}`}
                        className="px-3 py-2.5 rounded-xl border"
                        style={{ background: '#FFFBEB', borderColor: '#FDE68A' }}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold" style={{ color: '#92400E' }}>{f.contrat_numero || f.contrat_id}</p>
                            <p className="text-xs truncate" style={{ color: '#A16207' }}>{f.client_nom}</p>
                            <p className="text-xs" style={{ color: '#B45309' }}>{fmtDate(f.periode_debut)} → {fmtDate(f.periode_fin)}</p>
                            <p className="text-xs font-semibold tabular-nums" style={{ color: '#92400E' }}>{fmtEur(f.total_ht)} HT</p>
                            {f.odoo_invoice_name && <p className="text-xs font-mono truncate" style={{ color: '#A16207' }}>{f.odoo_invoice_name}</p>}
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button onClick={() => syncerFacture(f.facture_id)} disabled={syncingId === f.facture_id}
                              data-testid={`btn-sync-${f.facture_id}`} title="Synchroniser depuis Odoo"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium"
                              style={{ borderColor: '#BAE6FD', background: '#F0F9FF', color: '#0369A1' }}>
                              {syncingId === f.facture_id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                              <span>Sync</span>
                            </button>
                            <button onClick={() => purgerFacture(f.facture_id, f.contrat_numero || f.contrat_id)}
                              disabled={purgingId === f.facture_id}
                              data-testid={`btn-purger-${f.facture_id}`} title="Supprimer cette facture locale"
                              className="p-1.5 rounded-lg border" style={{ borderColor: '#FECACA', background: '#FEF2F2' }}>
                              {purgingId === f.facture_id ? <Loader2 size={12} className="animate-spin" style={{ color: '#B91C1C' }} /> : <Trash2 size={12} style={{ color: '#B91C1C' }} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Liste filtrée */}
            <div className="px-3 pt-3 pb-3 space-y-1.5">
              {filteredContrats.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <Search size={24} style={{ color: 'var(--text-disabled)' }} />
                  <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                    Aucun contrat pour ce filtre
                  </p>
                  <button onClick={() => { setActiveFilter(null); setSearch(''); }}
                    className="text-xs underline" style={{ color: 'var(--primary-solid)' }}>
                    Réinitialiser
                  </button>
                </div>
              ) : filteredContrats.map(c => {
                const isActive = selected?.contrat_id === c.contrat_id;
                const st = STATUT_STYLES[c.statut] || { bg: '#F3F4F6', color: '#6B7280', label: c.statut };
                return (
                  <button
                    key={c.contrat_id}
                    onClick={() => { factureCreeContratIdRef.current = null; setFactureCreeData(null); setSelected(c); }}
                    data-testid={`aide-fac-row-${c.contrat_id}`}
                    className="w-full text-left px-3 py-3 rounded-xl border transition-all"
                    style={{
                      borderColor: isActive ? 'var(--primary-solid)' : 'var(--border)',
                      background: isActive ? 'var(--primary-50)' : 'white',
                      boxShadow: isActive ? '0 0 0 1px var(--primary-solid)' : 'none',
                    }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-bold truncate"
                            style={{ color: isActive ? 'var(--primary-solid)' : 'var(--text)' }}>
                            {c.numero}
                          </p>
                          {c.nb_avenants > 0 && (
                            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                              style={{ background: '#EDE9FE', color: '#7C3AED' }}>
                              {c.nb_avenants} avenant{c.nb_avenants > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          {c.client_nom}
                        </p>
                      </div>
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: st.bg, color: st.color }}>
                        {st.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                        {c.nb_lignes} ligne{c.nb_lignes !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm font-bold tabular-nums"
                        style={{ color: isActive ? 'var(--primary-solid)' : 'var(--text)' }}>
                        {fmtEur(c.total_a_facturer)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Colonne droite : détail ── */}
          <div className="flex-1 overflow-hidden bg-white">
            <DetailPanel
              contrat={selected}
              facturant={facturant}
              onFacturer={facturer}
              factureCree={factureCreeData?.contrat_id === selected?.contrat_id ? factureCreeData : null}
              onOpenPlPanel={openPlDraftPanel}
            />
          </div>
        </div>
      )}

      {/* ── Panel brouillon Pennylane (identique Ventes.js) ── */}
      {plDraftPanel !== null && createPortal(
        <div
          data-radix-portal=""
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            pointerEvents: 'auto',
          }}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) closePlDraftPanel(); }}
        >
          <div
            style={{
              position: 'fixed', top: 0, right: 0,
              width: 'min(1360px, 92vw)', height: '100vh',
              background: '#ffffff', boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
              display: 'flex', flexDirection: 'column',
              pointerEvents: 'auto',
            }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 24px', borderBottom: '1px solid #E5E7EB', flexShrink: 0,
            }}>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Brouillon Pennylane</h2>
                <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0 0' }}>
                  Vérifiez la facture puis validez ou saisissez le règlement
                </p>
              </div>
              <button
                onClick={closePlDraftPanel}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280' }}>
                <X size={18} />
              </button>
            </div>

            {/* Corps — PDF ou spinner */}
            <div style={{ flex: 1, overflow: 'hidden', background: '#F9FAFB' }}>
              {plDraftPanel.loading ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12,
                }}>
                  <Loader2 size={32} className="animate-spin" style={{ color: '#0EA5E9' }} />
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Chargement du PDF…</span>
                </div>
              ) : plDraftPanel.pdf_blob_url ? (
                <iframe
                  src={plDraftPanel.pdf_blob_url}
                  title="Brouillon facture Pennylane"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12, padding: 32,
                }}>
                  <FileText size={40} style={{ color: '#D1D5DB' }} />
                  <p style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', margin: 0 }}>
                    PDF non disponible pour le moment.
                  </p>
                </div>
              )}
            </div>

            {/* Footer — actions */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 24px', borderTop: '1px solid #E5E7EB', gap: 10, flexShrink: 0,
            }}>
              {/* Supprimer brouillon — masqué si facture finalisée */}
              {factureCreeData?.ext_status !== 'posted' ? (
                <button
                  data-testid="pl-draft-loc-btn-supprimer"
                  onClick={handleSupprimerBrouillonPennylane}
                  disabled={supprimantDraftPL || validantPL}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'none', border: '1px solid #FECACA', color: '#DC2626',
                    cursor: 'pointer', opacity: (supprimantDraftPL || validantPL) ? 0.5 : 1,
                  }}>
                  {supprimantDraftPL ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Supprimer brouillon
                </button>
              ) : <span />}

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={closePlDraftPanel}
                  style={{
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'none', border: '1px solid #E5E7EB', color: '#374151', cursor: 'pointer',
                  }}>
                  Fermer
                </button>
                {/* Saisir / Voir le règlement */}
                <button
                  data-testid="pl-draft-loc-btn-reglement"
                  onClick={() => setShowReglementPanel(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: reglementSaisi ? 'rgba(34,197,94,0.1)' : 'none',
                    border: reglementSaisi ? '1px solid rgba(34,197,94,0.5)' : '1px solid #E5E7EB',
                    color: reglementSaisi ? '#16a34a' : '#374151', cursor: 'pointer',
                  }}>
                  <CreditCard size={13} />
                  {reglementSaisi
                    ? (factureCreeData?.ext_status === 'posted' ? 'Voir le règlement' : 'Règlement saisi ✓')
                    : 'Saisir le règlement'}
                </button>
                {/* Valider la facture — masqué si déjà finalisée ou si comptant sans règlement */}
                {factureCreeData?.ext_status !== 'posted' && (selected?.mode_reglement !== 'comptant' || reglementSaisi) && (
                  <button
                    data-testid="pl-draft-loc-btn-valider"
                    onClick={handleValiderPennylane}
                    disabled={validantPL || supprimantDraftPL}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                      background: '#0EA5E9', color: '#ffffff', border: 'none',
                      cursor: 'pointer', opacity: (validantPL || supprimantDraftPL) ? 0.5 : 1,
                    }}>
                    {validantPL ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Valider la facture
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Panel de saisie du règlement — portal au-dessus du panel PDF (zIndex 10500) */}
      {showReglementPanel && factureCreeData?.facture_id && typeof document !== 'undefined' && createPortal(
        <Dialog.Root open={true}>
          <Dialog.Content
            data-ignore-outside
            asChild
            onInteractOutside={e => e.preventDefault()}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <div style={{ position: 'fixed', inset: 0, zIndex: 10500, pointerEvents: 'none' }}>
              <ReglementPanel
                devis={{ vente_id: factureCreeData.facture_id, numero: factureCreeData.ext_invoice_number || factureCreeData.facture_id }}
                onClose={() => setShowReglementPanel(false)}
                onSaved={(updatedData) => {
                  setReglementSaisi(updatedData?.reglements?.length > 0 ? updatedData : null);
                  setShowReglementPanel(false);
                }}
                apiFns={{
                  get:    () => api.getReglementsFactureLoc(factureCreeData.facture_id),
                  create: (d) => api.createReglementFactureLoc(factureCreeData.facture_id, d),
                  delete: (rid) => api.deleteReglementFactureLoc(factureCreeData.facture_id, rid),
                }}
              />
            </div>
          </Dialog.Content>
        </Dialog.Root>,
        document.body
      )}
    </div>
  );
}
